import { LitElement, css } from 'lit';
import type { CSSResultGroup } from 'lit';
import { tokens } from '../styles/tokens.css.js';
import { base } from '../styles/base.css.js';
import type { FlowContext } from '../state/flow-context.js';

/**
 * Shared base for all PinakesSSO elements.
 *
 * - Injects design tokens (CSS custom properties) + the base reset into every shadow root.
 * - Subclasses compose their own styles: `static styles = [SsoElement.styles, css`...`]`.
 * - Offers small a11y helpers (focus management, dispatching flow-advance events).
 *
 * Pages and widgets extend `SsoElement` instead of `LitElement`.
 */
export class SsoElement extends LitElement {
  static override styles: CSSResultGroup = [tokens, base];

  /**
   * Move focus to the page heading on route entry so screen readers announce the new page.
   * Call from `firstUpdated()`.
   */
  protected focusHeading(): void {
    void this.updateComplete.then(() => {
      const h = this.renderRoot.querySelector<HTMLElement>('h1');
      if (h) {
        h.setAttribute('tabindex', '-1');
        h.focus();
      }
    });
  }

  private hasAutoFocused = false;

  /**
   * Move focus to the first actionable control on entry — the first input/code-field, or failing
   * that the first button/tile/link — so the user can type or act immediately instead of landing on
   * static heading text. Prefer this over {@link focusHeading} on pages with a form or primary action.
   *
   * Acts at most once per element and only once a control has rendered, so it is safe to call from
   * `updated()` for pages whose content arrives asynchronously (spinner first). Our custom controls
   * (`sso-text-field`, `sso-code-input`, `sso-button`, `sso-app-tile`) implement `focus()` to focus
   * their inner element after their own first render.
   */
  protected focusFirstControl(): void {
    if (this.hasAutoFocused) return;
    const root = this.renderRoot;
    if (!root) return;
    // Tier 1: somewhere to type or choose a value. A positive list (rather than `input:not(...)`)
    // so grid checkboxes / date cells in the admin editors aren't grabbed as the entry point.
    const inputLike =
      'sso-text-field,sso-code-input,textarea,select,' +
      'input[type="text"],input[type="email"],input[type="tel"],input[type="password"],' +
      'input[type="search"],input[type="url"],input[type="number"]';
    // Tier 2: a primary action — buttons/tiles before plain links, so a leading "‹ Back" link
    // doesn't win over the page's real action buttons.
    const buttonLike = 'sso-button,sso-app-tile,button,[role="button"]';
    const linkLike = 'a[href]';
    const el =
      root.querySelector<HTMLElement>(inputLike) ??
      root.querySelector<HTMLElement>(buttonLike) ??
      root.querySelector<HTMLElement>(linkLike);
    if (!el) return;
    // Don't steal focus onto a disabled/busy control — try again on the next update.
    if ((el as HTMLInputElement & HTMLButtonElement).disabled) return;
    this.hasAutoFocused = true;
    el.focus();
  }

  /**
   * The transient login-flow state held on the `<sso-app>` shell, if reachable from this element's
   * shadow tree. Pages use it to pass cached login methods / one-time recovery codes between screens.
   */
  protected get flowContext(): FlowContext | undefined {
    const host = (this.getRootNode() as ShadowRoot | undefined)?.host as
      | { flow?: FlowContext }
      | undefined;
    return host?.flow;
  }

  /**
   * Advance the login flow. `<sso-app>` listens for `flow-advance` and routes to the next step.
   * The detail is the server-issued `next` token ('choose' | 'totp' | 'enroll' | 'recovery' | 'launcher').
   */
  protected advanceFlow(next: string | undefined): void {
    this.dispatchEvent(
      new CustomEvent<string | undefined>('flow-advance', {
        detail: next,
        bubbles: true,
        composed: true,
      }),
    );
  }

  /**
   * Terminal handoff: the server already minted the single-use ticket and built the allowlisted,
   * language-prefixed URL. The SPA only navigates — it never constructs ticket URLs.
   */
  protected redirect(url: string): void {
    window.location.assign(url);
  }

  /**
   * Validates a `return_uri` query param before navigating to it. Accepts ONLY a same-origin,
   * site-relative path and returns it normalized; returns null for anything else — absolute URLs,
   * protocol-relative `//host`, and backslash-tricked `/\host` (which browsers treat as `//`) — so a
   * crafted `?return_uri=` cannot bounce the user off-site after login (open-redirect / phishing).
   */
  protected safeReturnUri(raw: string | null | undefined): string | null {
    if (!raw || !raw.startsWith('/') || raw.startsWith('//') || raw.startsWith('/\\')) return null;
    try {
      const u = new URL(raw, window.location.origin);
      if (u.origin !== window.location.origin) return null;
      return u.pathname + u.search + u.hash;
    } catch {
      return null;
    }
  }

  /**
   * Validates a URL fragment (`#…`) before it is re-attached to a return target. A fragment is never
   * sent to a server and can't redirect anywhere on its own, so the check only rejects malformed
   * values (must start with `#`, no whitespace / control characters, bounded length).
   */
  protected safeReturnHash(raw: string | null | undefined): string | null {
    if (!raw || !raw.startsWith('#') || raw.length < 2 || raw.length > 2048) return null;
    // Reject whitespace / control characters (0x00-0x20, DEL) without a regex escape.
    for (let i = 0; i < raw.length; i++) {
      const c = raw.charCodeAt(i);
      if (c <= 0x20 || c === 0x7f) return null;
    }
    return raw;
  }

  /**
   * The effective post-login return target. Prefers the value stashed on the flow context at login
   * (which survives the 2FA / enrollment navigations) and falls back to the current URL's `?return_uri=`.
   * Always re-validated through {@link safeReturnUri}. Null ⇒ no target (go to the launcher).
   *
   * Deep links of a hash-routed app (Callimachus: `callimachus.pinakes.be/#!/Beheer/Person/1`) live in
   * the URL fragment, which browsers carry across every 302 of the handoff chain but which a scripted
   * navigation (`location.assign`) drops. So the fragment that arrived with the login (stashed at the
   * same time as the return target) is re-attached here whenever the target has none of its own; the
   * authorize endpoint then 302s without a fragment and the browser carries it into the app.
   */
  protected resolveReturnUri(): string | null {
    const stashed = this.flowContext?.returnUri;
    const uri = stashed
      ? this.safeReturnUri(stashed)
      : this.safeReturnUri(new URLSearchParams(window.location.search).get('return_uri'));
    if (!uri || uri.includes('#')) return uri;

    const hash = this.safeReturnHash(this.flowContext?.returnHash ?? window.location.hash);
    return hash ? uri + hash : uri;
  }

  /** Raise a global toast/error on the shell (aria-live host). */
  protected toast(message: string, kind: 'info' | 'success' | 'warning' | 'error' = 'info'): void {
    this.dispatchEvent(
      new CustomEvent('sso-toast', {
        detail: { message, kind },
        bubbles: true,
        composed: true,
      }),
    );
  }
}

/** Re-exported empty style group so subclasses can `import { sharedStyles }` if preferred. */
export const sharedStyles = css``;
